/**
 * DM and group DM calls (2026-11-01), as Discord has them: the calls running
 * in your DMs (CALL_CREATE / CALL_UPDATE / CALL_DELETE) with who is in each
 * (DM voice states have no `guild_id`), and what you do with them: start or
 * join one (voice in the DM: the server starts the call and rings the
 * others), decline a ring, ring someone again.
 *
 * An incoming ring (you are rung, not in that call) plays the ring sound
 * while it lasts, as Settings > Sounds allows ("Calls", the volume, the
 * master switch) and never on Do Not Disturb or from people you ignore; with
 * the window in the background it also shows a desktop notification
 * (Settings > Notifications) and flashes the taskbar (the tab's title on the
 * web), through notify() (notify/notify.ts, the one entry point).
 * While you ring others from inside a call you hear a soft ringback.
 */
import {
  ChannelType,
  type GatewayDispatch,
  type Snowflake,
  type VoiceState,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { tr } from '../i18n';
import { api } from '../lib/api';
import { assetUrl } from '../lib/config';
import { endCallNotification, notifyIncomingCall } from '../notify/notify';
import { play } from '../sound/sounds';
import { toast } from '../ui/overlay';
import { joinVoice, leaveVoice, VoiceJoinError } from '../voice/controller';
import { useVoice } from '../voice/store';
import { flagOn, onConfigChange } from './config';
import { useData } from './data';
import { officialGuildId, privateChannelName } from './selectors';
import { goHub } from './ui';
import { isIgnored } from './um';

export interface DmCall {
  channelId: Snowflake;
  /** The call's CALL message. */
  messageId: Snowflake;
  /** Who is being rung now. */
  ringing: Snowflake[];
  /** Who is in it, by user. */
  states: Record<Snowflake, VoiceState>;
}

export type DmCalls = Record<Snowflake, DmCall>;

interface CallsStore {
  calls: DmCalls;
}

export const useCalls = create<CallsStore>(() => ({ calls: {} }));

const isPrivate = (type: number | undefined) =>
  type === ChannelType.DM || type === ChannelType.GROUP_DM;

/** One gateway frame applied to the calls (pure; unknown frames change nothing). */
export function applyCallFrame(calls: DmCalls, frame: GatewayDispatch): DmCalls {
  switch (frame.t) {
    case 'READY':
      // a new session: the running calls follow as CALL_CREATE
      return Object.keys(calls).length ? {} : calls;
    case 'CALL_CREATE': {
      const d = frame.d;
      const states: Record<Snowflake, VoiceState> = {};
      for (const v of d.voice_states) if (v.channel_id === d.channel_id) states[v.user_id] = v;
      return {
        ...calls,
        [d.channel_id]: {
          channelId: d.channel_id,
          messageId: d.message_id,
          ringing: [...d.ringing],
          states,
        },
      };
    }
    case 'CALL_UPDATE': {
      const d = frame.d;
      const cur = calls[d.channel_id];
      return {
        ...calls,
        [d.channel_id]: {
          channelId: d.channel_id,
          messageId: d.message_id,
          ringing: [...d.ringing],
          states: cur?.states ?? {},
        },
      };
    }
    case 'CALL_DELETE': {
      if (!calls[frame.d.channel_id]) return calls;
      const next = { ...calls };
      delete next[frame.d.channel_id];
      return next;
    }
    case 'CHANNEL_DELETE': {
      if (!isPrivate(frame.d.type) || !calls[frame.d.id]) return calls;
      const next = { ...calls };
      delete next[frame.d.id];
      return next;
    }
    case 'VOICE_STATE_UPDATE': {
      const v = frame.d;
      // a guild's voice state: not a call's
      if (v.guild_id) return calls;
      let next = calls;
      // out of any other call first (one connection per person)
      for (const c of Object.values(calls)) {
        if (c.channelId === v.channel_id || !c.states[v.user_id]) continue;
        const states = { ...c.states };
        delete states[v.user_id];
        next = { ...next, [c.channelId]: { ...c, states } };
      }
      if (!v.channel_id) return next;
      const c = next[v.channel_id] ?? {
        channelId: v.channel_id,
        messageId: '',
        ringing: [],
        states: {},
      };
      return {
        ...next,
        [v.channel_id]: {
          ...c,
          ringing: c.ringing.filter((u) => u !== v.user_id),
          states: { ...c.states, [v.user_id]: v },
        },
      };
    }
    default:
      return calls;
  }
}

/** Feeds one gateway dispatch into the calls (store/session.ts). */
export function feedCalls(frame: GatewayDispatch): void {
  const before = useCalls.getState().calls;
  const after = applyCallFrame(before, frame);
  if (after !== before) useCalls.setState({ calls: after });
  // out of a group you were in a call in: out of its call too
  if (frame.t === 'CHANNEL_DELETE' && isPrivate(frame.d.type) && inCallHere(frame.d.id))
    leaveVoice();
}

export function resetCalls(): void {
  useCalls.setState({ calls: {} });
}

/** The user ids in a call, in the order they came. */
export function callPeople(c: DmCall | undefined): Snowflake[] {
  return c ? Object.keys(c.states) : [];
}

/** Your voice session is in (or joining) this DM's call. */
export function inCallHere(channelId: Snowflake): boolean {
  const s = useVoice.getState().session;
  return s.status !== 'idle' && (s.channelId ?? s.targetId) === channelId;
}

/** The calls ringing you now (you are rung there, and not in it). */
export function incomingCalls(
  calls: DmCalls,
  meId: Snowflake | undefined,
  here: Snowflake | null,
): DmCall[] {
  if (!meId) return [];
  return Object.values(calls).filter(
    (c) => c.ringing.includes(meId) && !c.states[meId] && c.channelId !== here,
  );
}

/** Calls are on for you: voice and the Console's `dm_calls`. */
export function callsOn(): boolean {
  return flagOn('voice') && flagOn('dm_calls');
}

/**
 * Starts the call in a DM, or joins the one running there (Accept): voice
 * in the DM, which opens. Refusals are said in a toast.
 */
export async function joinCall(channelId: Snowflake): Promise<boolean> {
  const { t } = tr();
  if (!callsOn()) {
    toast(t('call.off'), { icon: 'phone', tone: 'warn' });
    return false;
  }
  const d = useData.getState();
  const c = d.channels[channelId];
  const gid = officialGuildId(d);
  if (!c || !gid) return false;
  goHub({ kind: 'home', channelId });
  // from another DM's call: out of it first, so the join takes a fresh token
  // (that call's room may have left the voice tree since its token)
  const from = sessionRoom();
  if (from && from !== channelId && isPrivate(d.channels[from]?.type)) leaveVoice();
  try {
    await joinVoice(channelId, { guildId: gid });
    return true;
  } catch (e) {
    const code = e instanceof VoiceJoinError ? e.code : 'unknown';
    // the toast says why: a call that never started leaves no red voice
    // state in the dock (QA NV-04)
    const s = useVoice.getState().session;
    if (s.status === 'failed' && (s.targetId ?? s.channelId) === channelId) leaveVoice();
    toast(
      code === 'not_allowed'
        ? t('call.refused')
        : t('call.couldNot', {
            why: t(`ns.voice.err.${code}`, { channel: privateChannelName(d, c) }),
          }),
      { icon: 'alert', tone: 'danger' },
    );
    return false;
  }
}

/** Declines a call ringing you (Decline): its ring stops for you on every device. */
export async function declineCall(channelId: Snowflake): Promise<void> {
  const me = useData.getState().me?.id;
  // stopped here at once; the server tells every session
  useCalls.setState((s) => {
    const c = s.calls[channelId];
    if (!c || !me) return s;
    return {
      calls: { ...s.calls, [channelId]: { ...c, ringing: c.ringing.filter((u) => u !== me) } },
    };
  });
  try {
    await api.calls.stopRinging(channelId);
  } catch {
    /* it stops by itself after 30 seconds */
  }
}

/** Rings members again from inside the call (all who are not in it, or `userIds`). */
export async function ringAgain(channelId: Snowflake, userIds?: Snowflake[]): Promise<void> {
  try {
    await api.calls.ring(channelId, userIds);
  } catch {
    toast(tr().t('call.ringFailed'), { icon: 'alert', tone: 'warn' });
  }
}

/* --------------------------- the ring, outside --------------------------- */

/** How often the ring sound repeats while a call rings you (or you ring others). */
export const RING_EVERY_MS = 2_600;

/** Is the ring for these calls silent (Do Not Disturb, or only people you ignore in them)? */
function silent(c: DmCall): boolean {
  if (useData.getState().settings?.status === 'dnd') return true;
  const people = callPeople(c);
  return people.length > 0 && people.every((u) => isIgnored(u));
}

/** What should sound now: your incoming ring, the ringback while you ring others, or nothing. */
export function ringSound(calls: DmCalls, meId: Snowflake | undefined, here: Snowflake | null) {
  const incoming = incomingCalls(calls, meId, here).filter((c) => !silent(c));
  if (incoming.length) return 'ring' as const;
  const mine = here ? calls[here] : undefined;
  if (mine && meId && mine.states[meId] && mine.ringing.some((u) => u !== meId))
    return 'ringback' as const;
  return null;
}

let ringTimer: ReturnType<typeof setInterval> | undefined;
let ringing: 'ring' | 'ringback' | null = null;

function sessionRoom(): Snowflake | null {
  const s = useVoice.getState().session;
  return s.status === 'idle' ? null : (s.channelId ?? s.targetId);
}

function syncRing(): void {
  const meId = useData.getState().me?.id;
  const want = ringSound(useCalls.getState().calls, meId, sessionRoom());
  if (want === ringing) return;
  ringing = want;
  clearInterval(ringTimer);
  ringTimer = undefined;
  if (!want) return;
  play(want);
  ringTimer = setInterval(() => play(want), RING_EVERY_MS);
}

/**
 * The OS notification for a ring that just started: notify() shows it only
 * with the window in the background (in view, the incoming call card is
 * there), flashes the taskbar until focused, and a click opens the DM.
 */
function notifyRing(c: DmCall): void {
  const d = useData.getState();
  if (silent(c)) return;
  const ch = d.channels[c.channelId];
  if (!ch) return;
  const { t } = tr();
  const people = callPeople(c).filter((u) => u !== d.me?.id);
  // a DM's caller is its one other person (ignored or blocked: no toast); a group's is the group
  const from = ch.type === ChannelType.DM && people.length === 1 ? people[0]! : null;
  const user = from ? d.users[from] : undefined;
  notifyIncomingCall({
    channelId: c.channelId,
    from,
    who: privateChannelName(d, ch),
    icon: user ? assetUrl(user.avatar ?? null) : null,
    body: t(ch.type === ChannelType.GROUP_DM ? 'call.os.group' : 'call.os.dm'),
  });
}

let wasIncoming = new Set<Snowflake>();

/** New rings get their notification; stopped ones lose it. */
function syncNotices(): void {
  const meId = useData.getState().me?.id;
  const now = incomingCalls(useCalls.getState().calls, meId, sessionRoom());
  const ids = new Set(now.map((c) => c.channelId));
  for (const c of now) if (!wasIncoming.has(c.channelId)) notifyRing(c);
  for (const id of wasIncoming) {
    if (ids.has(id)) continue;
    endCallNotification(id);
  }
  wasIncoming = ids;
}

let started = false;

/** Starts the ring and the notifications (once, at start). */
export function startCallAlerts(): void {
  if (started) return;
  started = true;
  const sync = () => {
    syncRing();
    syncNotices();
  };
  useCalls.subscribe(sync);
  useVoice.subscribe((s, p) => {
    if (s.session.status !== p.session.status || s.session.channelId !== p.session.channelId)
      sync();
    // a DM call that ended under you (a block, the group left) is not a moderator's doing
    const was = p.session.channelId ?? p.session.targetId;
    if (
      s.session.error === 'kicked' &&
      p.session.error !== 'kicked' &&
      was &&
      isPrivate(useData.getState().channels[was]?.type)
    ) {
      leaveVoice();
      toast(tr().t('call.ended'), { icon: 'phone-off' });
    }
  });
  useData.subscribe((d, p) => {
    if (d.settings?.status !== p.settings?.status || d.me !== p.me) sync();
  });
}

// the Console turned calls off while you were in one: leave it calmly
onConfigChange((next, prev) => {
  // (voice turned off has its own notice: hub/voiceUi.ts)
  if (!prev || flagOn('dm_calls', next) || !flagOn('dm_calls', prev)) return;
  const room = sessionRoom();
  if (!room || !isPrivate(useData.getState().channels[room]?.type)) return;
  leaveVoice();
  toast(tr().t('call.off'), { icon: 'phone', tone: 'warn' });
});
